/** * メッセージ詳細コンポーネント */
<template>
  <div class="content">
    <div class="content-box">
      <loading-mask v-bind:isLoading="isLoading">
        <div class="box-inner-message-detail" v-if="messageDetail">
          <p v-if="isMessageTypeSend" class="send-date">
            {{ $t('appMessageDetail.sendDateTime') }}
            {{ sendDateText.formattedDate }} ({{ sendDateText.diffText }})
          </p>
          <template v-if="messageDetail">
            <!-- ユーザー(送信者/送信先)一覧 -->
            <div class="message-detail-user-list">
              <div
                class="message-detail-user"
                v-for="user in usersForCard"
                v-bind:key="user.id"
              >
                <!-- 送信者/宛先ユーザー -->
                <card-user
                  can-user-name-click
                  v-bind:user-id="user.id"
                  v-bind:user-name="user.userName"
                  v-bind:groups="user.groups"
                  v-on:usercard-username-click="
                    $store.dispatch('updateProfileUserId', $event)
                  "
                >
                  <template v-slot:content-append>
                    <div v-if="isMessageTypeSend" class="read-info">
                      <span class="text">
                        {{
                          user.updateDate
                            ? $t('appMessageDetail.read')
                            : $t('appMessageDetail.unread')
                        }}
                      </span>
                      <span class="text">{{ user.updateDateText || '-' }}</span>
                    </div>
                  </template>
                </card-user>
              </div>
            </div>
          </template>
          <div class="message-body">
            <template v-if="messageId">
              <div class="message-body-top">
                <h3 class="content-title">
                  {{ $t('appMessageDetail.message') }}
                </h3>
                <div class="right">
                  <span v-if="!isMessageTypeSend" class="date-text">
                    {{
                      `${$t('appMessageDetail.receiveDateTime')} ${
                        sendDateText.text
                      }`
                    }}
                  </span>
                  <b-button
                    class="reply-button"
                    variant="secondary"
                    size="sm"
                    v-t="'appMessageDetail.reply'"
                    v-on:click="$_onClickReply"
                  />
                </div>
              </div>
              <!-- メール本文 -->
              <div class="message-content">{{ messageDetail.content }}</div>
              <!-- データリスト -->
              <data-list
                v-if="hasSchedule"
                v-bind:data="scheduleDataForList"
                class="appendix-list"
              >
                <template v-slot:schedule="{ item }">
                  <a class="link" v-on:click="$_onClickScheduleTitle(item.id)">
                    {{ item.title }}
                  </a>
                </template>
              </data-list>
              <!-- 添付ファイル -->
              <div v-if="attachFiles.length" class="attach-file-col">
                <attach-file-viewer
                  gap
                  v-bind:attachFiles="attachFiles"
                  v-bind:recordId="sendMessageId"
                />
              </div>
            </template>
          </div>
          <!-- 地図 -->
          <div class="map-box" v-if="hasSendPosition">
            <map-control
              ref="map-control"
              class="map-content"
              v-bind="$store.getters.mapControlBinds"
              v-bind:id-prefix="`${componentPrefix}message-`"
              v-bind:height="'250px'"
              v-bind:center="messageCenter"
              v-bind:zoom="18"
              v-bind:layer-renderers="renderers"
              v-bind:store-sync="['mapType']"
              v-bind:control="{ header: false, layerMenu: false }"
              v-bind:can-change-indoor-level="false"
              v-bind:reactive-render="!isModalOverlaid"
              v-on:map-control-initend="$_onMapInitEnd"
            />
          </div>
          <!-- 返信用メッセージ送信ダイアログ -->
          <message-post-dialog
            ref="message-post-dialog"
            v-bind:default-receive-users="toUsers"
            v-bind:reply-content="messageDetail.content"
          />
        </div>
      </loading-mask>
    </div>
    <!-- スケジュール詳細ダイアログ -->
    <schedule-detail
      ref="schedule-detail-dialog"
      v-bind:idPrefix="`message${messageId}`"
    />
    <!-- モーダル重畳検知 -->
    <modal-overlay-detector
      v-on:is-modal-overlaid-changed="$_onModalOverlayChanged"
    />
  </div>
</template>

<script>
import { inject, ref, shallowRef, computed } from 'vue'
import store from '@/store'
import AttachFileViewer from '@/components/AppAttachFileViewer.vue'
import CardUser from '@/components/AppCardUser.vue'
import DataList from '@/components/AppDataList.vue'
import MessagePostDialog from '@/components/message/AppMessagePostDialog.vue'
import ScheduleDetail from '@/views/TheScheduleDetail.vue'
import BvModalOverlayDetector from '@/components/AppBvModalOverlayDetector.vue'
import DomainRenderer from '@/js/map-renderer/DomainRenderer.js'
import { RestApiClient, ATTRIBUTE_TYPE } from 'ifield-core-components'
import log from 'loglevel'
import { toLocalTz } from '@/js/TimezoneHelper'
import { DATE_FORMAT_HYPHEN, TIME_FORMAT_MILLISECOND } from '@/constants.js'
import IconPin from '@/assets/ifield/message-pin@3x.png'
import ImageUtil from '@/js/util/ImageUtil.js'
import { useDateDisplayText } from '@/composables/UseDateDisplayText.js'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

/**
 * メッセージタイプ
 */
const MESSAGE_TYPES = {
  RECEIVE: 'receive',
  SEND: 'send',
}

/**
 * ミリ秒日時フォーマット
 */
const DATETIME_FORMAT_MILLISECOND = `${DATE_FORMAT_HYPHEN}T${TIME_FORMAT_MILLISECOND}`

// レイヤーID
const LAYER_ID = 'message'

// レンダラーファクトリ
const rendererFactory = new DomainRenderer({ layerId: LAYER_ID })

// iconMapping
const MAPPING = {
  default: {
    x: 0,
    y: 0,
    width: 168,
    height: 192,
    anchorX: 84,
    anchorY: 168,
  },
}

/**
 * Dateへ変換
 * @param {string} date 変換したい日時文字列
 * @returns {Date}
 */
const toDate = (date) => (date ? new Date(date) : null)

/**
 * メッセージ詳細データを保持するクラス
 * MEMO:各種 getter はリアクティブにならない
 */
class MessageDetail {
  /**
   * コンストラクタ
   * @param {Object} messageType メッセージタイプ
   * @param {Object} message メッセージのAPIレスポンス
   */
  constructor(messageType, message) {
    this.messageType = messageType
    this.message = message
    this.users = this.makeUsers()
  }

  /**
   * ユーザー一覧情報を作成する
   */
  makeUsers() {
    let users = []
    if (this.messageType === MESSAGE_TYPES.RECEIVE) {
      // 受信メッセージの場合、送信者を表示する
      let profileImageAttachFileType = this.message.send_user_profile_image
        ? this.message.send_user_profile_image.attach_file_type
        : null
      users.push({
        id: this.message.send_user_id,
        userName: this.message.send_user_name,
        profileImageAttachFileType: profileImageAttachFileType,
        updateDate: toDate(this.message.send_at),
      })
    } else if (this.messageType === MESSAGE_TYPES.SEND) {
      // 送信メッセージの場合、宛先ユーザーを表示する
      users = this.message.receive_users.map((user) => {
        return {
          id: user.receive_user_id,
          userName: user.receive_user_name,
          profileImageAttachFileType: user.receive_user_profile_image
            ? user.receive_user_profile_image.attach_file_type
            : null,
          updateDate: toDate(user.read_at),
        }
      })
    } else {
      log.error(`unsupported message type. (${this.messageType})`)
    }
    return users
  }

  /**
   * ユーザーID
   * @type {Array}
   */
  get id() {
    return this.message.id
  }

  /**
   * スケジュールID
   * @type {Number}
   */
  get scheduleId() {
    return this.message.schedule_id
  }

  /**
   * スケジュールタイトル
   * @type {String}
   */
  get scheduleTitle() {
    return this.message.schedule_title
  }

  /**
   * 添付ファイル
   * @type {Array}
   */
  get attachFiles() {
    return this.message.message_attach_files
  }

  /**
   * メッセージ本文
   * @return {string}
   */
  get content() {
    return this.message.content
  }

  /**
   * 送信メッセージID
   * @return {string}
   */
  get sendMessageId() {
    if (this.messageType === MESSAGE_TYPES.RECEIVE) {
      return this.message.send_message_id
    } else if (this.messageType === MESSAGE_TYPES.SEND) {
      return this.message.id
    } else {
      log.error(`unsupported message type. (${this.messageType})`)
    }
    return null
  }

  /**
   * 送信日時
   */
  get sendDate() {
    return this.message.send_at
  }

  /**
   * 送信位置情報を持っているかどうか
   * @return {boolean}
   */
  get hasSendPosition() {
    if (this.message.geom) {
      return true
    }
    return false
  }

  /**
   * 送信位置
   * @return {object}
   */
  get geom() {
    return this.message.geom
  }
}

export default {
  name: 'AppMessageDetail',
  components: {
    'attach-file-viewer': AttachFileViewer,
    'card-user': CardUser,
    DataList,
    'message-post-dialog': MessagePostDialog,
    'schedule-detail': ScheduleDetail,
    'modal-overlay-detector': BvModalOverlayDetector,
  },
  props: {
    /**
     * メッセージタイプ(receive|send)
     */
    messageType: {
      type: String,
      required: true,
      validator: (value) => {
        return [MESSAGE_TYPES.RECEIVE, MESSAGE_TYPES.SEND].includes(value)
      },
    },
    /**
     * 子コンポーネントのプレフィックス
     * 本コンポーネントを複数もつ場合に設定してください
     */
    componentPrefix: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup(props, context) {
    // 共通処理
    const { handleApiError } = useHandleApiError(props, context)
    const baseDate = inject('baseDate')

    /**
     * 表示対象のメッセージID
     * @type {Ref.<number>}
     */
    const messageId = ref(null)
    /**
     * メッセージ詳細
     * @type {ShallowRef.<MessageDetail>}
     */
    const messageDetail = shallowRef(null)

    /**
     * 送信日時に関する表示文字列情報(formattedDate, diffText)
     * @type {ComputedRef.<Object>}
     */
    const sendDateText = computed(() => {
      if (!messageDetail.value) return {}
      const { formattedDate, diffText, text } = useDateDisplayText({
        date: messageDetail.value.sendDate,
        baseDate,
      })
      return {
        formattedDate: formattedDate.value,
        diffText: diffText.value,
        text: text.value,
      }
    })

    /**
     * ユーザーデータ(カード用)
     * @type {ComputedRef.<Object[]>}
     */
    const usersForCard = computed(() => {
      if (!messageDetail.value) return []
      return messageDetail.value.users.map((u) => {
        const { text } = useDateDisplayText({ date: u.updateDate, baseDate })
        return {
          ...u,
          groups: store.getters['user/userMap'].get(u.id)?.groups || [],
          updateDateText: text.value,
        }
      })
    })

    return {
      baseDate,
      handleApiError,
      messageId,
      messageDetail,
      sendDateText,
      usersForCard,
    }
  },
  data: function () {
    return {
      /**
       * モーダルが重畳している場合はtrue
       * @type {Boolean}
       */
      isModalOverlaid: false,
      /**
       * ロード中フラグ
       */
      isLoading: false,
      /**
       * iconAtlas
       * @type {Image}
       */
      iconAtlas: null,
      /**
       * 自レイヤーのレンダラー
       * @type {Object}
       */
      myRenderer: null,
    }
  },

  computed: {
    /**
     * メッセージタイプが送信済みの場合にtrue
     * @return {boolean}
     */
    isMessageTypeSend: function () {
      return this.messageType === MESSAGE_TYPES.SEND
    },
    /**
     * 地図binding用render関数配列
     * @type {Array.<Function>}
     */
    renderers: function () {
      const renderers = [...this.$store.getters['map/layerRenderers']]
      if (this.myRenderer) {
        renderers.push(this.myRenderer.render)
      }
      return renderers
    },
    /**
     * メッセージが送信位置を持っている場合にtrue
     */
    hasSendPosition: function () {
      return this.messageDetail && this.messageDetail.hasSendPosition
    },
    /**
     * メッセージの中心座標
     */
    messageCenter: function () {
      if (
        this.hasSendPosition &&
        this.messageDetail.geom.geometry &&
        this.messageDetail.geom.geometry.coordinates
      ) {
        const [lng, lat] = this.messageDetail.geom.geometry.coordinates
        return { lat, lng }
      }
      return null
    },
    /**
     * 返信時の宛先ユーザー一覧
     * @type {number[]}
     */
    toUsers: function () {
      if (!this.messageDetail || !this.messageDetail.users) {
        return null
      }
      return this.messageDetail.users.map((user) => ({
        id: user.id,
        role_id: user.role_id,
      }))
    },
    /**
     * 送信メッセージID
     * @type {Number}
     */
    sendMessageId: function () {
      return this.messageDetail && this.messageDetail.sendMessageId
    },
    /**
     * 添付ファイル
     * @type {Array.<Object>}
     */
    attachFiles: function () {
      return this.messageDetail?.attachFiles || []
    },
    /**
     * スケジュールがある場合はtrue
     * @type {boolean}
     */
    hasSchedule: function () {
      return !!this.messageDetail?.scheduleId
    },
    /**
     * スケジュール表示用データ
     * @type {Object}
     */
    scheduleDataForList: function () {
      return {
        standard: [
          {
            type: ATTRIBUTE_TYPE.SLOT,
            label: this.$t('appMessageDetail.field.task'),
            name: 'schedule',
            id: this.messageDetail?.scheduleId || null,
            title: this.messageDetail?.scheduleTitle || '',
          },
        ],
      }
    },
  },
  watch: {
    messageDetail: function () {
      this.hasSendPosition && this.$_updateMessageLayer()
    },
  },
  created: async function () {
    try {
      this.iconAtlas = await ImageUtil.toImage(IconPin)
    } catch (error) {
      this.$log.warn('failed to load message pin. cannot plot to map', error)
    }
  },
  mounted: function () {
    this.$log.info('AppMessageDetail mounted.')
  },
  methods: {
    /**
     * メッセージを表示する
     * @param {number} messageId 表示対象のメッセージID
     */
    show: async function (messageId) {
      this.messageId = messageId
      if (this.messageType === MESSAGE_TYPES.RECEIVE) {
        await this.$_showReceiveMessageDetail()
      } else if (this.messageType === MESSAGE_TYPES.SEND) {
        await this.$_showSentMessageDetail()
      } else {
        this.$log.error('Invalid messageType', this.messageType)
      }
    },

    /**
     * 受信メッセージ詳細を表示する
     */
    $_showReceiveMessageDetail: async function () {
      this.$_showLoading()
      try {
        // メッセージ詳細を取得する
        const url = RestApiClient.createApiUrl(
          `receive_message/${this.messageId}`
        )
        const d = await RestApiClient.executeGetApi(url).then(
          ({ data }) => data
        )
        const message = {
          ...d,
          send_at: toLocalTz(d.send_at, DATETIME_FORMAT_MILLISECOND),
          read_at: toLocalTz(d.read_at, DATETIME_FORMAT_MILLISECOND),
        }
        this.messageDetail = new MessageDetail(MESSAGE_TYPES.RECEIVE, message)

        await this.$nextTick()

        // メッセージを既読にする
        if (!message.read_flg) {
          this.$_markMessageAsRead(this.messageId)
        }
      } catch (error) {
        // エラーが発生した場合は詳細データを削除する
        this.messageDetail = null
        this.handleApiError(error)
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * 送信済みメッセージ詳細を表示する
     */
    $_showSentMessageDetail: async function () {
      this.$_showLoading()
      try {
        // メッセージ詳細を取得する
        const url = RestApiClient.createApiUrl(`send_message/${this.messageId}`)
        const d = await RestApiClient.executeGetApi(url).then(
          ({ data }) => data
        )
        const message = {
          ...d,
          send_at: toLocalTz(d.send_at, DATETIME_FORMAT_MILLISECOND),
          receive_users: d.receive_users.map((u) => ({
            ...u,
            read_at: toLocalTz(u.read_at, DATETIME_FORMAT_MILLISECOND),
          })),
        }
        this.messageDetail = new MessageDetail(MESSAGE_TYPES.SEND, message)

        await this.$nextTick()
      } catch (error) {
        // エラーが発生した場合は詳細データを削除する
        this.messageDetail = null
        this.handleApiError(error)
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * 対象の受信メッセージを既読にする
     * @param {number} 既読にする受信メッセージID
     */
    $_markMessageAsRead: function (messageId) {
      RestApiClient.executePutApi(
        RestApiClient.createApiUrl(
          `receive_message/read_flg/myaccount/${messageId}`
        )
      )
    },

    /**
     * メッセージレイヤー更新
     */
    $_updateMessageLayer: function () {
      if (!this.iconAtlas) {
        this.$log.warn('message pin icon not found!')
        return
      }

      const props = {
        iconAtlas: this.iconAtlas,
        iconMapping: MAPPING,
        getIcon: () => 'default',
      }
      this.myRenderer = rendererFactory.createRenderer(
        [this.messageDetail.message],
        props
      )
    },

    /**
     * マップ初期化完了時処理
     */
    $_onMapInitEnd: function () {
      this.map = this.$refs['map-control']
    },

    /**
     * スケジュールタイトルをクリックした時の処理
     * @param {number} scheduleId 表示対象のスケジュールID
     */
    $_onClickScheduleTitle: function (scheduleId) {
      if (!scheduleId) {
        return
      }

      // スケジュール詳細を表示する
      this.$refs['schedule-detail-dialog'].showModal(scheduleId)
    },

    /**
     * 返信用のメッセージ送信ダイアログを表示する
     */
    $_onClickReply: function () {
      this.$refs['message-post-dialog'].showModal()
    },

    /**
     * モーダル重畳状態変更時処理
     * @param {Boolean} value モーダル重畳状態
     */
    $_onModalOverlayChanged: function (value) {
      this.isModalOverlaid = value
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.content {
  height: 100%;
}

.content-box {
  height: 100%;
}

.box-inner-message-detail {
  height: 100%;
  overflow-y: auto;
  @include mixin-scrollbar;

  .send-date {
    color: $text-color;
    background-color: #f6f6f6;
    font-size: 0.8em;
    margin: 0;
    padding: 18px 10px;
    text-align: right;
  }
}

.message-detail-user-list {
  div:last-of-type {
    border-bottom: none;
  }
}

.message-detail-user {
  padding: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.read-info {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  row-gap: 8px;

  .text {
    color: #959595;
    font-size: $font-size-small;
    line-height: 1;
  }
}

.message-body {
  width: 100%;
  color: $text-color;
  background-color: #f6f6f6;
  padding: 20px;
  padding-bottom: 28px;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 20px;

  .message-content {
    font-size: 0.8em;
    white-space: pre-line;
    overflow-wrap: anywhere;
  }
}

.message-body-top {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 16px;

  .content-title {
    font-size: 1em;
    font-weight: bold;
    margin-bottom: 0;
  }

  .right {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    column-gap: 12px;

    .date-text {
      color: #959595;
      font-size: $font-size-small;
      line-height: 1;
    }

    .reply-button {
      font-size: 0.8em;
      height: 30px;
    }
  }
}

.appendix-list {
  ::v-deep .list-wrapper {
    margin: 0;
  }

  .link {
    cursor: pointer;
    color: $text-color-link;
    font-weight: bold;

    &:hover {
      text-decoration: underline;
    }
  }
}

.attach-file-col {
  width: 100%;
  padding: 0;
}

.map-box {
  background-color: #f6f6f6;
  padding: 0 0 0 0;
}

.map-content {
  margin: 0;
}
</style>
